import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { ArianeNavComponent } from 'src/app/layout/ariane-nav/ariane-nav.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatStepperModule } from '@angular/material/stepper';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { InformationsGeneralesFormComponent } from './informations-generales-form/informations-generales-form.component';
import { CaracteristiquesGeneralesFormComponent } from './caracteristiques-generales-form/caracteristiques-generales-form.component';
import { MotivationDemandeFormComponent } from './motivation-demande-form/motivation-demande-form.component';
import { DestinatairesDemandeFormComponent } from './destinataires-demande-form/destinataires-demande-form.component';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { DemandeRecrutementService } from 'src/app/core/services/demande-recrutement.service';
import { MatDialog } from '@angular/material/dialog';
import { ValidationModalComponent } from 'src/app/shared/validation-modal/validation-modal.component';
import { finalize, take, Subject, takeUntil } from 'rxjs';
import { FormStateService } from 'src/app/core/services/form-state.service';
import { Router } from '@angular/router';
import { Poste } from '@models/domains/posts';
import { ReferentielsService } from 'src/app/core/services/referentiels.service';
import { StatusAvis } from '@models/enums';
import { noWhitespaceValidator } from 'src/app/shared/utils/no-whitespace-validator';
import { RecruitmentUtils } from 'src/app/shared/utils/recruitment.utils';

@Component({
    selector: 'app-recrutement',
    imports: [
        CommonModule,
        MatIconModule,
        MatCardModule,
        MatDividerModule,
        ArianeNavComponent,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        MatButtonModule,
        MatStepperModule,
        InformationsGeneralesFormComponent,
        CaracteristiquesGeneralesFormComponent,
        MotivationDemandeFormComponent,
        DestinatairesDemandeFormComponent
    ],
    templateUrl: './recrutement.component.html',
    styleUrls: ['./recrutement.component.scss']
})
export class RecrutementComponent implements OnInit, OnDestroy {
  arianeLabel: string = 'Formulaire saisies demandes de recrutement';
  arianeIcon: string = 'Contract';
  arianeList = [
    {
      label: 'Formulaire saisies demandes de recrutement',
      url: '/pama/recrutement'
    }
  ];

  formTitles = ['Informations générales', 'Caractéristiques du poste', 'Motivation de la demande', 'Destinataires de la demande'];
  parentForm: FormGroup;
  currentStepIndex: number = 0;
  currentTitle = this.formTitles[this.currentStepIndex];
  nextTitle = this.formTitles[this.currentStepIndex + 1];
  isSubmitting = false;

  detenteurPosteList: any[] = [];
  detenteurPosteReadOnly: boolean;
  selectedPosteDetails: Poste | null = null;
  selectedService: any = null;
  selectedDirection: any = null;
  attachedfiles: File[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private demandeRecrutementService: DemandeRecrutementService,
    public dialog: MatDialog,
    private formStateService: FormStateService,
    private router: Router,
    private referentielsService: ReferentielsService
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    // Monitor form changes for dirty state tracking
    this.parentForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const isDirty = this.parentForm.dirty && !this.parentForm.valid;
      this.formStateService.setFormState(isDirty);
    });

    // Monitor typeRecrutement changes to handle vacataire field requirements
    this.step1
      .get('typeRecrutement')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Update field states and requirements based on new recruitment type
        // NOTE: Do NOT clear Step 2 data here - only clear when post is deselected
        this.updateFieldStatesForVacataire();
      });

    const serviceControl = this.step1.get('service');
    if (serviceControl) {
      this.selectedService = serviceControl.value ?? null;
      serviceControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((service) => {
        this.selectedService = service;
      });
    }

    const directionControl = this.step1.get('direction');
    if (directionControl) {
      this.selectedDirection = directionControl.value ?? null;
      directionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((direction) => {
        this.selectedDirection = direction;
      });
    }

    // Set initial state
    this.updateFieldStatesForVacataire();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.parentForm = this.fb.group({
      // Form Info Genérales
      step1: this.fb.group({
        typeRecrutement: ['', Validators.required],
        pole: [{ value: '', disabled: true }, Validators.required],
        direction: [{ value: '', disabled: true }, Validators.required],
        service: ['' /*, Validators.required*/],
        numeroPoste: ['', Validators.required],
        motifLiberation: ['', Validators.required],
        intituleposte: ['', Validators.required]
      }),
      // Form Cracteristiques du poste
      step2: this.fb.group(
        {
          etatPoste: [{ value: '', disabled: true }, Validators.required],
          dateRecrutement: ['', Validators.required],
          occupationPoste: [{ value: '', disabled: true }, Validators.required],
          detenteurPoste: [{ value: '', disabled: true }],
          categoriePoste: [{ value: '', disabled: true }],

          posteBudgete: [{ value: null, disabled: true }, Validators.required],
          dateDepart: [''],
          interne: [false],
          externe: [false]
        },
        { validators: this.atLeastOneCheckboxCheckedValidator(['interne', 'externe']) }
      ),
      // Form Motivation de la demande
      step3: this.fb.group({
        motivation: ['', [Validators.required, this.maxLengthValidator(720), noWhitespaceValidator]],
        files: [[], [Validators.required, this.validateFiles]]
      }),
      // Form Destinataire de la demande
      step4: this.fb.group({
        destinataireResponsable: ['', Validators.required],
        destinataireValidationDirecteur: ['', Validators.required],
        destinataireChargeGpeec: ['', Validators.required],
        destinataireDrh: ['', Validators.required],
        destinataireDg: ['', Validators.required],
        destinataireDgs: ['', Validators.required],
        destinataireArchivageRh: ['', Validators.required]
      })
    });
  }

  // Validator functions
  maxLengthValidator(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && value.length > maxLength) {
        return { maxLengthExceeded: { value: control.value } };
      }
      return null;
    };
  }

  atLeastOneCheckboxCheckedValidator(keys: string[]): ValidatorFn {
    return (formGroup: AbstractControl) => {
      const isValid = keys.some((key) => formGroup.get(key)?.value);
      return isValid ? null : { atLeastOneRequired: true };
    };
  }

  validateFiles(control: any) {
    const files = control.value;
    if (files && files.length > 0) {
      const invalidFiles = files.filter((file: File) => file.type !== 'application/pdf');
      return invalidFiles.length === 0 ? null : { invalidFileType: true };
    }
    return { required: true };
  }

  // Check if the current recruitment type is a "vacataire" type
  isVacataireType(): boolean {
    const typeRecrutement = this.step1.get('typeRecrutement')?.value;
    return RecruitmentUtils.isVacataireType(typeRecrutement);
  }

  // Utility methods to manage required validators
  private setFieldRequired(controlName: string, isRequired: boolean): void {
    const control = this.step1.get(controlName);
    if (!control) return;

    if (isRequired) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity();
  }

  private setFieldEnabled(controlName: string, isEnabledAndRequired: boolean): void {
    const control = this.step1.get(controlName);
    if (!control) return;

    if (isEnabledAndRequired) {
      control.setValidators([Validators.required]);
      control.enable();
    } else {
      control.clearValidators();
      control.disable();
      control.reset();
    }
    control.updateValueAndValidity();
  }

  private setStep2FieldsEnabled(isEnabled: boolean): void {
    // etatPoste is ALWAYS enabled for user input, regardless of recruitment type or post selection
    this.step2.get('etatPoste')?.enable();

    // Other fields follow the workflow rules based on recruitment type and post selection
    const apiControlledFields = ['occupationPoste', 'categoriePoste', 'posteBudgete'];
    apiControlledFields.forEach((fieldName) => {
      const control = this.step2.get(fieldName);
      if (!control) return;

      if (isEnabled) {
        control.enable();
      } else {
        control.disable();
      }
    });

    // Note: detenteurPoste state is handled separately in updateDetenteurPosteState()
  }

  private setStep2FieldsForVacataire(shouldEnable: boolean): void {
    const vacataireControlledFields = ['etatPoste', 'occupationPoste', 'categoriePoste'];

    vacataireControlledFields.forEach((fieldName) => {
      const control = this.step2.get(fieldName);
      if (!control) {
        return;
      }

      if (shouldEnable) {
        control.enable({ emitEvent: false });
      } else {
        control.clearValidators();
        control.reset(null, { emitEvent: false });
        control.disable({ emitEvent: false });
      }
      control.updateValueAndValidity({ emitEvent: false });
    });

    // Mandatory fields when enabled
    const mandatoryFields = ['etatPoste', 'occupationPoste'];
    mandatoryFields.forEach((fieldName) => {
      const control = this.step2.get(fieldName);
      if (!control) {
        return;
      }

      if (shouldEnable) {
        control.setValidators([Validators.required]);
      }
      control.updateValueAndValidity({ emitEvent: false });
    });

    // Note: detenteurPoste state is handled separately in updateDetenteurPosteState()
  }

  // Main method to update field states based on recruitment type
  private updateFieldStatesForVacataire(): void {
    const isVacataire = this.isVacataireType();
    const isPosteSelected = !!this.selectedPosteDetails;

    if (isVacataire) {
      // Vacataire: intitule and numero are readonly and empty
      this.setFieldEnabled('intituleposte', false);
      this.setFieldEnabled('numeroPoste', false);

      this.setStep2FieldsForVacataire(false);
    } else {
      // Standard recruitment: intitule and numero are required
      this.setFieldEnabled('intituleposte', true);
      this.setFieldEnabled('numeroPoste', true);

      this.setStep2FieldsForVacataire(true);

      // Note: Data population/clearing is handled by updatePosteDetails() method
      // This method only manages field states, not data
    }

    // Always handle detenteurPoste special logic after other field states are set
    this.updateDetenteurPosteState();
  }

  // Form getters for template access
  get step1() {
    return this.parentForm.get('step1') as FormGroup;
  }

  get step2() {
    return this.parentForm.get('step2') as FormGroup;
  }

  get step3() {
    return this.parentForm.get('step3') as FormGroup;
  }

  get step4() {
    return this.parentForm.get('step4') as FormGroup;
  }

  // Handle stepper navigation
  onStepChange(event: StepperSelectionEvent): void {
    this.currentStepIndex = event.selectedIndex;
    this.currentTitle = this.formTitles[this.currentStepIndex];
    this.nextTitle = this.currentStepIndex < 3 ? this.formTitles[this.currentStepIndex + 1] : '';
  }

  // Event handlers from child components

  updatePosteDetails(details: Poste | undefined) {
    this.selectedPosteDetails = details ?? null;

    // Update field states based on the new post selection
    this.updateFieldStatesForVacataire();

    if (details) {
      // Post selected: load detenteur data and populate Step 2 fields
      this.loadDetenteurPoste(details.id);
    } else {
      // No post selected: clear all related data
      this.detenteurPosteList = [];
      this.clearStep2PostData();

      // For vacataire without post, ensure detenteurPoste is enabled for manual input
      if (this.isVacataireType()) {
        this.step2.get('detenteurPoste')?.enable();
        this.detenteurPosteReadOnly = false;
      } else {
        this.step2.get('detenteurPoste')?.disable();
        this.detenteurPosteReadOnly = true;
      }
    }
  }

  handleFileSelected(files: File[]) {
    this.attachedfiles = files;
  }

  loadDetenteurPoste(idPoste: string) {
    this.referentielsService
      .getDetenteurPoste(idPoste)
      .pipe(take(1))
      .subscribe((data: any) => {
        this.detenteurPosteList = data.detenteurs || [];

        this.step2.patchValue({
          etatPoste: data.etat,
          occupationPoste: data.occupation,
          categoriePoste: data.categoriePoste,
          posteBudgete: data.budgete
        });
        this.updateDetenteurPoste();
        this.step2.markAllAsTouched();
        this.step2.updateValueAndValidity();
      });
  }

  // Clear Step 2 post-related data (used when post is deselected)
  private clearStep2PostData(): void {
    this.step2.patchValue({
      etatPoste: null,
      occupationPoste: null,
      categoriePoste: null,
      posteBudgete: null,
      detenteurPoste: null
    });
  }

  // Handle detenteurPoste special state logic
  private updateDetenteurPosteState(): void {
    if (!this.selectedPosteDetails) {
      // No post selected
      if (this.isVacataireType()) {
        // Vacataire without post: enable for manual input
        this.step2.get('detenteurPoste')?.enable();
        this.detenteurPosteReadOnly = false;
      } else {
        // Standard without post: disable
        this.step2.get('detenteurPoste')?.disable();
        this.detenteurPosteReadOnly = true;
      }
    }
    // When post is selected, the state is handled in updateDetenteurPoste() after API call
  }

  private updateDetenteurPoste() {
    const selectedDetenteur = this.detenteurPosteList.length === 1 ? this.detenteurPosteList[0] : null;
    this.step2.patchValue({ detenteurPoste: selectedDetenteur });

    // Special detenteurPoste logic: enable if no holder found, disable if holder exists
    let detenteurPoste = this.step2.controls['detenteurPoste'].value;
    if (!detenteurPoste) {
      // No holder found: enable for manual input
      this.step2.controls['detenteurPoste'].enable();
      this.detenteurPosteReadOnly = false;
    } else {
      // Holder found: disable and show pre-filled data
      this.step2.controls['detenteurPoste'].disable();
      this.detenteurPosteReadOnly = true;
    }
  }

  // Form submission
  onSubmitForm() {
    if (this.isSubmitting || !this.parentForm.valid) {
      return;
    }
    this.isSubmitting = true;

    const demandeRecrutement = this.mapFormDataToRequest(this.parentForm.getRawValue());

    this.demandeRecrutementService
      .createDemandeRecrutement(demandeRecrutement, this.attachedfiles)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe({
        next: () => {
          this.dialog.closeAll();
          const dialogRef = this.dialog.open(ValidationModalComponent, {
            width: '600px',
            data: {
              title: 'Confirmation',
              content: 'La demande de recrutement a été créée avec succès.'
            },
            disableClose: true
          });
          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              this.router.navigate(['/pama/consultation']);
            }
          });
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
  }

  mapFormDataToRequest(formData: any): any {
    // Nom du détenteur
    let detenteurPoste = formData.step2?.detenteurPoste;
    let detenteurNom = null;
    if (typeof detenteurPoste === 'string') {
      detenteurNom = detenteurPoste;
      detenteurPoste = null;
    } else if (detenteurPoste?.nom || detenteurPoste?.prenom) {
      detenteurNom = [detenteurPoste.nom, detenteurPoste.prenom].filter(Boolean).join(' ') || null;
    }

    // Determine type recrutement souhaite
    let typeRecrutementSouhaite = 'Interne';
    if (formData.step2?.externe) {
      typeRecrutementSouhaite = formData.step2?.interne ? 'Interne_Externe' : 'Externe';
    }

    return {
      numPoste: formData.step1?.numeroPoste?.numero ?? null,
      posteBudgete: formData.step2?.posteBudgete || false,
      dateRecrSouhaite: formData.step2?.dateRecrutement ? formData.step2.dateRecrutement.toFormat('yyyy-MM-dd') : null,
      dateDepartDefinitif: formData.step2?.dateDepart ? formData.step2.dateDepart.toFormat('yyyy-MM-dd') : null,
      motivationDemande: formData.step3?.motivation ?? null,
      typeRecrutement: formData.step1?.typeRecrutement ? { id: formData.step1.typeRecrutement.id } : null,
      intitulePoste: formData.step1?.intituleposte ? { id: formData.step1.intituleposte.id } : null,
      typeRecrutementSouhaite,
      etatPoste: formData.step2?.etatPoste ?? null,
      emetteur: formData.step2?.emetteur ? { id: formData.step2.emetteur } : null,
      occupationPoste: formData.step2?.occupationPoste ?? null,
      detenteurPoste: detenteurPoste ?? null,
      detenteurNom: detenteurNom ?? null,
      categoriePoste: formData.step2?.categoriePoste ? { id: formData.step2.categoriePoste.id } : null,
      motifLiberationPoste: formData.step1?.motifLiberation ? { id: formData.step1.motifLiberation.id } : null,
      pole: formData.step1?.pole ? { id: formData.step1.pole.id } : null,
      statut: StatusAvis.A_VALIDER,
      destinataireValidation: formData.step4?.destinataireValidationDirecteur
        ? { id: formData.step4.destinataireValidationDirecteur.id }
        : null,
      destinataireCharge: formData.step4?.destinataireChargeGpeec ? { id: formData.step4.destinataireChargeGpeec.id } : null,
      destinataireDG: formData.step4?.destinataireDg ? { id: formData.step4.destinataireDg.id } : null,
      destinataireDRH: formData.step4?.destinataireDrh ? { id: formData.step4.destinataireDrh.id } : null,
      destinataireResponsable: formData.step4?.destinataireResponsable ? { id: formData.step4.destinataireResponsable.id } : null,
      destinataireDgs: formData.step4?.destinataireDgs ? { id: formData.step4.destinataireDgs.id } : null,
      destinataireArchivage: formData.step4?.destinataireArchivageRh ? { id: formData.step4.destinataireArchivageRh.id } : null,
      direction: formData.step1?.direction ? { id: formData.step1.direction.id } : null,
      serviceFonctionnel: formData.step1?.service ? { id: formData.step1.service.id } : null
    };
  }
}
